import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, CheckCircle, XCircle, Trash2, CheckSquare, Clock, AlertTriangle } from 'lucide-react'
import { tasksApi, usersApi, leadsApi, type Task } from '../../api/endpoints'
import { useAuthStore } from '../../stores/auth'
import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import Input, { Select, Textarea } from '../../components/ui/Input'
import { PriorityBadge, StatusBadge } from '../../components/ui/Badge'
import { fmtDate, istInputToUTC } from '../../utils/date'
import Pagination from '../../components/ui/Pagination'
import EmptyState from '../../components/ui/EmptyState'
import LoadingState from '../../components/ui/LoadingState'
import MobileFilterPanel from '../../components/ui/MobileFilterPanel'
import { useConfirm } from '../../components/ui/ConfirmDialog'

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
]

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'demo', label: 'Demo' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'custom', label: 'Custom' },
]

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
]

export default function TasksPage() {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [viewTab, setViewTab] = useState<'active' | 'completed'>('active')
  const [filters, setFilters] = useState({
    status: '', task_type: '', priority: '', assigned_to: '', search: '',
    due_from: '', due_to: '', sort_by: 'due_date', sort_dir: 'asc',
  })
  const [showDateFilters, setShowDateFilters] = useState(false)
  const [offset, setOffset] = useState(0)
  const limit = 25
  const [modalOpen, setModalOpen] = useState(false)
  const [detailTaskId, setDetailTaskId] = useState<number | null>(null)
  const [confirmNode, confirmDialog] = useConfirm()

  const { data: usersListData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    enabled: user?.role === 'admin' || user?.role === 'manager',
  })
  const usersList = usersListData?.users || []

  const params: Record<string, string> = { limit: String(limit), offset: String(offset), sort_by: filters.sort_by, sort_dir: filters.sort_dir }
  if (viewTab === 'completed') {
    params.status = filters.status || 'completed,cancelled'
  } else if (filters.status) {
    params.status = filters.status
  } else {
    params.status = 'pending,overdue'
  }
  if (filters.task_type) params.task_type = filters.task_type
  if (filters.priority) params.priority = filters.priority
  if (filters.assigned_to) params.assigned_to = filters.assigned_to
  if (filters.due_from) params.due_after = filters.due_from
  if (filters.due_to) params.due_before = filters.due_to

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', params],
    queryFn: () => tasksApi.list(params),
  })

  const { data: overdueData } = useQuery({
    queryKey: ['tasks', 'overdue'],
    queryFn: tasksApi.overdue,
  })

  const completeMutation = useMutation({
    mutationFn: (id: number) => tasksApi.complete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  })

  const cancelMutation = useMutation({
    mutationFn: (id: number) => tasksApi.cancel(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => tasksApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  })

  const tasks = data?.tasks || []
  const total = data?.total || 0
  const overdueCount = overdueData?.total || 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
          {overdueCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
              <AlertTriangle size={12} /> {overdueCount} overdue
            </span>
          )}
        </div>
        <Button onClick={() => setModalOpen(true)}>
          <Plus size={16} /> Add Task
        </Button>
      </div>

      {/* Active / Completed tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => { setViewTab('active'); setOffset(0) }}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            viewTab === 'active' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Active
        </button>
        <button
          onClick={() => { setViewTab('completed'); setOffset(0) }}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            viewTab === 'completed' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Completed
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <MobileFilterPanel activeCount={[filters.status, filters.task_type, filters.priority, filters.assigned_to].filter(Boolean).length + (filters.sort_by !== 'due_date' ? 1 : 0) + (filters.due_from || filters.due_to ? 1 : 0)}>
          <Select options={STATUS_OPTIONS} value={filters.status} onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setOffset(0) }} />
          <Select options={TYPE_OPTIONS} value={filters.task_type} onChange={(e) => { setFilters(f => ({ ...f, task_type: e.target.value })); setOffset(0) }} />
          <Select
            options={[{ value: '', label: 'All Priorities' }, ...PRIORITY_OPTIONS]}
            value={filters.priority}
            onChange={(e) => { setFilters(f => ({ ...f, priority: e.target.value })); setOffset(0) }}
          />
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <Select
              options={[
                { value: '', label: 'All Assignees' },
                ...usersList.map((u: any) => ({ value: String(u.id), label: u.full_name || u.username })),
              ]}
              value={filters.assigned_to}
              onChange={(e) => { setFilters(f => ({ ...f, assigned_to: e.target.value })); setOffset(0) }}
            />
          )}
          <Select
            options={[
              { value: 'due_date', label: 'Sort: Due Date' },
              { value: 'created_at', label: 'Sort: Created' },
              { value: 'priority', label: 'Sort: Priority' },
              { value: 'title', label: 'Sort: Title' },
            ]}
            value={filters.sort_by}
            onChange={(e) => { setFilters(f => ({ ...f, sort_by: e.target.value })); setOffset(0) }}
          />
          <button
            onClick={() => setFilters(f => ({ ...f, sort_dir: f.sort_dir === 'asc' ? 'desc' : 'asc' }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            title={filters.sort_dir === 'asc' ? 'Ascending' : 'Descending'}
          >
            {filters.sort_dir === 'asc' ? 'Asc' : 'Desc'}
          </button>
          <button
            onClick={() => setShowDateFilters(!showDateFilters)}
            className={`px-3 py-2 border rounded-lg text-xs font-medium transition-colors ${
              showDateFilters || filters.due_from || filters.due_to
                ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            Due Date
          </button>
        </MobileFilterPanel>
      </div>

      {/* Due date range filter */}
      {showDateFilters && (
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs text-gray-500">Due between:</span>
          <input
            type="date"
            value={filters.due_from}
            onChange={(e) => { setFilters(f => ({ ...f, due_from: e.target.value })); setOffset(0) }}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <span className="text-xs text-gray-400">to</span>
          <input
            type="date"
            value={filters.due_to}
            onChange={(e) => { setFilters(f => ({ ...f, due_to: e.target.value })); setOffset(0) }}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-1 focus:ring-indigo-500"
          />
          {(filters.due_from || filters.due_to) && (
            <button
              onClick={() => { setFilters(f => ({ ...f, due_from: '', due_to: '' })); setOffset(0) }}
              className="text-xs text-red-500 hover:text-red-700"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <LoadingState message="Loading tasks..." />
      ) : tasks.length === 0 ? (
        <EmptyState
          icon={CheckSquare}
          title="No tasks found"
          description="Create a task to track your sales activities"
          action={<Button onClick={() => setModalOpen(true)}><Plus size={16} /> Add Task</Button>}
        />
      ) : (
        <>
          <div className="space-y-2">
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onComplete={() => completeMutation.mutate(task.id)}
                onCancel={() => cancelMutation.mutate(task.id)}
                onDelete={async () => { const ok = await confirmDialog({ message: 'Are you sure you want to delete this task? This cannot be undone.', title: 'Delete Task', confirmLabel: 'Delete', confirmVariant: 'danger' }); if (!ok) return; deleteMutation.mutate(task.id) }}
                onClick={() => setDetailTaskId(task.id)}
                isAdmin={user?.role === 'admin' || user?.role === 'manager'}
              />
            ))}
          </div>
          <Pagination total={total} limit={limit} offset={offset} onChange={setOffset} />
        </>
      )}

      <TaskFormModal open={modalOpen} onClose={() => setModalOpen(false)} />

      {detailTaskId && (
        <TaskDetailModal taskId={detailTaskId} onClose={() => setDetailTaskId(null)} />
      )}

      {confirmNode}
    </div>
  )
}

function TaskCard({ task, onComplete, onCancel, onDelete, onClick, isAdmin }: {
  task: Task; onComplete: () => void; onCancel: () => void; onDelete: () => void; onClick: () => void; isAdmin: boolean
}) {
  const isTerminal = ['completed', 'cancelled'].includes(task.status)

  return (
    <div onClick={onClick} className={`bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:shadow-sm transition-shadow ${
      task.status === 'overdue' ? 'border-l-4 border-l-red-400' : ''
    } ${isTerminal ? 'opacity-60' : ''}`}>
      {/* Top row: complete button + title + badges */}
      <div className="flex items-start gap-3">
        {/* Complete button */}
        {!isTerminal && (
          <button
            onClick={(e) => { e.stopPropagation(); onComplete() }}
            className="w-8 h-8 sm:w-6 sm:h-6 rounded-full border-2 border-gray-300 hover:border-green-500 hover:bg-green-50 flex items-center justify-center flex-shrink-0 mt-0.5"
            title="Complete"
          >
            <CheckCircle size={14} className="text-gray-300 hover:text-green-500" />
          </button>
        )}
        {isTerminal && (
          <div className="w-8 h-8 sm:w-6 sm:h-6 flex-shrink-0 mt-0.5">
            {task.status === 'completed' && <CheckCircle size={18} className="text-green-500" />}
            {task.status === 'cancelled' && <XCircle size={18} className="text-gray-400" />}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm font-medium ${isTerminal ? 'line-through text-gray-400' : 'text-gray-900'}`}>{task.title}</p>
            <PriorityBadge priority={task.priority} />
            <StatusBadge status={task.status} />
            {task.is_recurring && <span className="text-xs text-indigo-600 font-medium">&#8635; Recurring</span>}
          </div>
          {/* Meta row */}
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
            <span className="capitalize">{task.task_type.replace('_', ' ')}</span>
            {task.lead_name && (
              <a
                href={`/leads/${(task as any).lead_id}`}
                onClick={(e) => e.stopPropagation()}
                className="text-indigo-600 hover:text-indigo-800 font-medium"
              >
                {task.lead_name}
              </a>
            )}
            {task.company_name && <span>{task.company_name}</span>}
            {isAdmin && task.assigned_user_name && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                &rarr; {task.assigned_user_name}
              </span>
            )}
          </div>
        </div>

        {/* Actions - desktop only inline */}
        <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
          {task.due_date && (
            <div className={`flex items-center gap-1 text-xs mr-2 ${task.status === 'overdue' ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
              <Clock size={12} />
              {fmtDate(task.due_date)}
            </div>
          )}
          {!isTerminal && (
            <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
              <button onClick={onCancel} className="p-1.5 text-gray-400 hover:text-amber-600 rounded" title="Cancel" aria-label="Cancel task"><XCircle size={15} /></button>
              {isAdmin && <button onClick={onDelete} className="p-1.5 text-gray-400 hover:text-red-600 rounded" title="Delete" aria-label="Delete task"><Trash2 size={15} /></button>}
            </div>
          )}
        </div>
      </div>

      {/* Bottom row - mobile only */}
      <div className="flex items-center justify-between mt-3 sm:hidden">
        <div className={`flex items-center gap-1 text-xs ${task.status === 'overdue' ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
          {task.due_date && (
            <>
              <Clock size={12} />
              {fmtDate(task.due_date)}
            </>
          )}
        </div>
        {!isTerminal && (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button onClick={onCancel} className="p-2.5 text-gray-400 hover:text-amber-600 rounded" title="Cancel" aria-label="Cancel task"><XCircle size={15} /></button>
            {isAdmin && <button onClick={onDelete} className="p-2.5 text-gray-400 hover:text-red-600 rounded" title="Delete" aria-label="Delete task"><Trash2 size={15} /></button>}
          </div>
        )}
      </div>
    </div>
  )
}

function TaskFormModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [form, setForm] = useState({
    title: '',
    description: '',
    task_type: 'custom',
    priority: 'medium',
    due_date: '',
    assigned_to: String(user?.id || 1),
    is_recurring: false,
    recurrence_rule: '',
    lead_id: '',
  })
  const [recurrenceHelpOpen, setRecurrenceHelpOpen] = useState(false)
  const [leadSearch, setLeadSearch] = useState('')

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    enabled: open && (user?.role === 'admin' || user?.role === 'manager'),
  })

  const { data: leadsData } = useQuery({
    queryKey: ['leads-for-task', leadSearch],
    queryFn: () => leadsApi.list({ search: leadSearch, limit: '10' }),
    enabled: open && leadSearch.length >= 2,
  })

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => tasksApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setForm({ title: '', description: '', task_type: 'custom', priority: 'medium', due_date: '', assigned_to: String(user?.id || 1), is_recurring: false, recurrence_rule: '', lead_id: '' })
      setLeadSearch('')
      onClose()
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const data: Record<string, unknown> = {
      ...form,
      assigned_to: parseInt(form.assigned_to),
    }
    // The input is IST wall-clock time; the API stores UTC.
    if (form.due_date) data.due_date = istInputToUTC(form.due_date)
    else delete data.due_date
    if (!form.description) delete data.description
    if (!form.is_recurring) { delete data.recurrence_rule; delete data.is_recurring }
    mutation.mutate(data)
  }

  const userOptions = (usersData?.users || []).map(u => ({ value: String(u.id), label: u.full_name || u.username }))
  if (userOptions.length === 0) userOptions.push({ value: String(user?.id || 1), label: user?.full_name || user?.username || 'Me' })

  return (
    <Modal open={open} onClose={onClose} title="New Task">
      <form onSubmit={handleSubmit} className="space-y-4">
        {mutation.error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{(mutation.error as Error).message}</div>}
        <Input label="Title *" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} required />
        <Textarea label="Description" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select label="Type" options={TYPE_OPTIONS.filter(o => o.value)} value={form.task_type} onChange={(e) => setForm(f => ({ ...f, task_type: e.target.value }))} />
          <Select label="Priority" options={PRIORITY_OPTIONS} value={form.priority} onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))} />
          <Input label="Due Date" type="datetime-local" value={form.due_date} onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))} />
          <Select label="Assign To" options={userOptions} value={form.assigned_to} onChange={(e) => setForm(f => ({ ...f, assigned_to: e.target.value }))} />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="recurring" checked={form.is_recurring} onChange={(e) => setForm(f => ({ ...f, is_recurring: e.target.checked }))} className="rounded" />
          <label htmlFor="recurring" className="text-sm text-gray-700">Recurring task</label>
        </div>
        {form.is_recurring && (
          <div>
            <Input label="Recurrence Rule" value={form.recurrence_rule} onChange={(e) => setForm(f => ({ ...f, recurrence_rule: e.target.value }))} placeholder="e.g. FREQ=WEEKLY;BYDAY=MO,WE,FR" />
            {!recurrenceHelpOpen ? (
              <button type="button" onClick={() => setRecurrenceHelpOpen(true)} className="text-xs text-indigo-600 hover:underline mt-1">Show examples</button>
            ) : (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 space-y-1">
                <p className="font-semibold mb-1">Common Recurrence Rules:</p>
                <p><code className="bg-blue-100 px-1 rounded">FREQ=DAILY</code> — Every day</p>
                <p><code className="bg-blue-100 px-1 rounded">FREQ=WEEKLY;BYDAY=MO,WE,FR</code> — Mon/Wed/Fri</p>
                <p><code className="bg-blue-100 px-1 rounded">FREQ=WEEKLY;BYDAY=MO</code> — Every Monday</p>
                <p><code className="bg-blue-100 px-1 rounded">FREQ=MONTHLY;BYMONTHDAY=1</code> — 1st of every month</p>
                <p><code className="bg-blue-100 px-1 rounded">FREQ=MONTHLY;BYMONTHDAY=15</code> — 15th of every month</p>
                <button type="button" onClick={() => setRecurrenceHelpOpen(false)} className="text-indigo-600 hover:underline mt-1">Hide</button>
              </div>
            )}
          </div>
        )}
        {/* Link to lead (optional) — only for admin/manager/sales_rep */}
        {(user?.role === 'admin' || user?.role === 'manager' || user?.role === 'sales_rep') && <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Link to Lead (optional)</label>
          <input
            type="text"
            value={leadSearch}
            onChange={(e) => setLeadSearch(e.target.value)}
            placeholder="Search leads by name..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {(leadsData?.leads || []).length > 0 && leadSearch.length >= 2 && (
            <div className="mt-1 border border-gray-200 rounded-lg max-h-32 overflow-y-auto">
              {(leadsData?.leads || []).map((l: any) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => { setForm(f => ({ ...f, lead_id: String(l.id) })); setLeadSearch(l.name) }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 flex items-center justify-between"
                >
                  <span>{l.name}</span>
                  <span className="text-xs text-gray-400">{l.lead_id || l.company || ''}</span>
                </button>
              ))}
            </div>
          )}
          {form.lead_id && <p className="text-xs text-green-600 mt-1">Linked to lead #{form.lead_id}</p>}
        </div>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={mutation.isPending}>Create Task</Button>
        </div>
      </form>
    </Modal>
  )
}

function TaskDetailModal({ taskId, onClose }: { taskId: number; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [newNote, setNewNote] = useState('')

  const { data: taskData } = useQuery({
    queryKey: ['task', taskId],
    queryFn: () => tasksApi.get(taskId),
  })

  const { data: notesData } = useQuery({
    queryKey: ['task-notes', taskId],
    queryFn: () => tasksApi.getNotes(taskId),
  })

  const addNoteMutation = useMutation({
    mutationFn: (content: string) => tasksApi.addNote(taskId, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-notes', taskId] })
      setNewNote('')
    },
  })

  const task = taskData?.data
  const notes: any[] = notesData?.data || []

  if (!task) return null

  return (
    <Modal open onClose={onClose} title={task.title || 'Task Details'} size="lg">
      <div className="space-y-4">
        {/* Task info */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Type:</span> <span className="capitalize">{task.task_type}</span></div>
          <div><span className="text-gray-500">Priority:</span> <PriorityBadge priority={task.priority} /></div>
          <div><span className="text-gray-500">Status:</span> <StatusBadge status={task.status} /></div>
          <div><span className="text-gray-500">Due:</span> {task.due_date ? fmtDate(task.due_date) : '—'}</div>
        </div>
        {task.description && <p className="text-sm text-gray-600">{task.description}</p>}

        {/* Notes section */}
        <div className="border-t border-gray-100 pt-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Notes</h4>
          <div className="mb-3">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
            <div className="flex justify-end mt-1">
              <Button size="sm" onClick={() => { if (newNote.trim()) addNoteMutation.mutate(newNote.trim()) }} loading={addNoteMutation.isPending} disabled={!newNote.trim()}>
                Add Note
              </Button>
            </div>
          </div>
          {notes.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-2">No notes yet</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {notes.map((n: any) => (
                <div key={n.id} className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{n.content}</p>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-400">
                    <span className="font-medium text-gray-500">{n.created_by_name || 'System'}</span>
                    <span>&middot;</span>
                    <span>{fmtDate(n.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
